import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Notice } from '@/components/Modal';
import {
  fetchPreviewPdf,
  issuanceFileUrl,
  useRequestIssuance,
  type Issuance,
} from '@/pages/mypage/useIssuance';
import { safeHref } from '@/lib/utils/url';

/**
 * 대화 안에서 증명서를 확인하고 신청하는 카드 (docs/18 §10.2 확인 게이트).
 *
 * 에이전트는 `preview`·`action` 이벤트로 **초안만** 내린다. 실제 신청은 여기서 사용자가 버튼을
 * 눌러야 일어나고, 그 호출은 **프론트가 세션 인증으로 직접** 한다(§5.4) — 위임 토큰으로는 쓰기가
 * 막혀 있고(`DELEGATION_SCOPE_DENIED`), 그래야 감사 로그의 "누가" 가 사람으로 남는다.
 *
 * 미리보기 PDF 와 신청은 마이페이지 발급 탭이 쓰는 {@link fetchPreviewPdf}·{@link useRequestIssuance}
 * 를 **그대로 재사용한다.** 두 화면이 서로 다른 경로로 발급하면 "미리보기와 다른 증명서가 나왔다" 가
 * 가능해진다 — 같은 입력, 같은 호출이어야 한다.
 *
 * PDF 에는 **"미승인" 워터마크가 박혀 있고 직인이 없다.** 서버가 렌더할 때 합성하므로 지울 수 없다.
 */
export type IssuanceDraft = {
  templateKey: string;
  /** 사람이 읽는 서식 이름. 없으면 templateKey 를 그대로 보여 준다. */
  templateName?: string;
  purpose?: string | null;
  recipient?: string | null;
  copies?: number | null;
};

export function IssuancePreviewCard({
  draft,
  onExecuted,
}: {
  draft: IssuanceDraft;
  /** 신청이 <b>성공했을 때만</b>. 대화 이력에 한 줄을 남겨 다음 턴이 알게 한다(`runAction` 과 같은 계약). */
  onExecuted?: (receipt: string) => void;
}) {
  const navigate = useNavigate();
  const requestIssuance = useRequestIssuance();

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<Issuance | null>(null);

  // blob URL 은 명시적으로 풀지 않으면 탭이 살아 있는 동안 PDF 가 메모리에 남는다.
  // 대화는 카드를 여러 개 만들 수 있어 페이지보다 더 잘 샌다.
  const urlRef = useRef<string | null>(null);
  useEffect(() => {
    urlRef.current = previewUrl;
  }, [previewUrl]);
  useEffect(() => () => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  // 카드가 뜨면 곧바로 미리보기를 받는다 — "확인해 주세요" 라고 말해 놓고 버튼을 한 번 더 누르게
  // 하지 않는다. 서식·용도는 이미 대화에서 정해졌다.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const url = await fetchPreviewPdf({
          templateKey: draft.templateKey,
          purpose: draft.purpose ?? undefined,
          recipient: draft.recipient ?? undefined,
        });
        if (!alive) {
          URL.revokeObjectURL(url);
          return;
        }
        setPreviewUrl(url);
      } catch (e) {
        if (alive) setFailure(e instanceof Error ? e.message : '미리보기를 만들지 못했습니다.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [draft.templateKey, draft.purpose, draft.recipient]);

  const submit = async () => {
    setFailure(null);
    try {
      // 미리보기와 **같은 입력**으로 신청한다. 눈으로 확인한 그 문서가 그대로 신청되어야 한다.
      const created = await requestIssuance.mutateAsync({
        templateKey: draft.templateKey,
        purpose: draft.purpose ?? undefined,
        recipient: draft.recipient ?? undefined,
        copies: draft.copies ?? 1,
      });
      setSubmitted(created);
      // 상태를 이력에도 적는다 — 「방금 신청한 거 어떻게 됐어」에 답하려면 결재 대기인지
      // 즉시 발급인지가 함께 있어야 한다.
      onExecuted?.(
        [
          '[실행됨] 증명서 발급 신청',
          draft.templateName ?? draft.templateKey,
          draft.purpose ? `용도 ${draft.purpose}` : null,
          draft.recipient ? `제출처 ${draft.recipient}` : null,
          `${draft.copies ?? 1}부`,
          created.status === 'ISSUED'
            ? `즉시 발급 완료${created.docNo ? ` (${created.docNo})` : ''}`
            : '인사팀 결재 대기',
        ].filter(Boolean).join(' · '),
      );
    } catch (e) {
      setFailure(e instanceof Error ? e.message : '신청하지 못했습니다.');
    }
  };

  const name = draft.templateName ?? draft.templateKey;

  return (
    <div className="rounded-2xl bg-white overflow-hidden" style={{ border: '1px solid var(--border)' }}>
      <div className="px-4 py-3 flex items-center justify-between gap-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate" style={{ color: 'var(--text-primary)' }}>{name}</p>
          <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
            {[draft.purpose && `용도 ${draft.purpose}`, draft.recipient && `제출처 ${draft.recipient}`,
              `${draft.copies ?? 1}부`].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className="text-xs px-2 py-1 rounded-full flex-shrink-0"
              style={{ background: 'var(--bg-hover)', color: 'var(--text-muted)' }}>
          미승인
        </span>
      </div>

      {loading && (
        <p className="px-4 py-6 text-sm text-center" style={{ color: 'var(--text-muted)' }}>미리보기를 만드는 중…</p>
      )}

      {previewUrl && (
        <iframe title={`${name} 미리보기`} src={safeHref(previewUrl)} className="w-full" style={{ height: '460px', border: 'none' }} />
      )}

      <div className="px-4 py-3 space-y-2" style={{ borderTop: '1px solid var(--border)' }}>
        {failure && <Notice tone="error">{failure}</Notice>}

        {submitted ? (
          // 결재를 타는 서식인지는 **서버 응답 상태로만** 안다. 화면이 서식별 규칙을 따로 알면
          // 규정이 바뀔 때 두 곳이 어긋난다.
          //
          // 신청은 끝이 아니라 시작이다. **다음에 무엇을 기다리고 어디서 보는지**까지 적고,
          // 그 화면으로 가는 버튼을 함께 둔다 — 대화는 지나가면 사라지지만 발급함은 남는다.
          <div className="space-y-2">
            <Notice tone={submitted.status === 'ISSUED' ? 'success' : 'info'}>
              {submitted.status === 'ISSUED' ? (
                <>
                  발급됐습니다{submitted.docNo ? ` (${submitted.docNo})` : ''}. 아래에서 열어 인쇄하시면 됩니다.
                  발급 내역은 마이페이지 발급함에 남습니다.
                </>
              ) : (
                <>
                  신청했습니다. <strong>인사팀 결재 대기</strong> 상태이며, 결재가 끝나면 문서번호와 직인이
                  찍힌 문서를 받으실 수 있습니다. 진행 상태는 전자결재에서, 완료된 문서는 마이페이지
                  발급함에서 확인하세요.
                </>
              )}
            </Notice>

            <div className="flex flex-wrap items-center gap-2">
              {submitted.status === 'ISSUED' && (
                <a className="btn-primary" href={issuanceFileUrl(submitted.id)} target="_blank" rel="noreferrer">
                  문서 열기 · 인쇄
                </a>
              )}
              {submitted.status === 'PENDING_APPROVAL' && (
                <button className="btn" onClick={() => navigate('/approval')}>전자결재 열기</button>
              )}
              <button className="btn" onClick={() => navigate('/mypage')}>발급함 열기</button>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={submit}
              disabled={requestIssuance.isPending || loading || !previewUrl}
              className="btn-primary"
            >
              {requestIssuance.isPending ? '신청 중…' : '네, 신청해 주세요'}
            </button>
            {previewUrl && (
              <a className="btn" href={safeHref(previewUrl)} target="_blank" rel="noreferrer">새 탭에서 열기</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
